/**
 * Canvas Serialization Types
 * Defines formats for saving, loading, and converting canvas workflows
 */

import { NodeInstance } from './nodeDefinition';
import { Connection } from './socket';
import { WorkflowGroup } from './workflowGroup';

// Re-export ApplicationInterface types for backward compatibility
export type { ApplicationInterface, InterfaceComponent } from './applicationInterface';

/**
 * Serialized Canvas Format
 * Complete representation of a canvas state for saving/loading
 */
export interface SerializedCanvas {
  id: string;
  name: string;
  description?: string;
  version: string;
  metadata: CanvasMetadata;
  nodes: SerializedNode[];
  connections: SerializedConnection[];
  groups: SerializedGroup[];
  layout: CanvasLayout;
  settings: CanvasSettings;
}

/**
 * Canvas Metadata
 */
export interface CanvasMetadata {
  createdAt: string;
  updatedAt: string;
  author?: string;
  tags: string[];
  category: string;
  thumbnail?: string; // Base64 or URL
  statistics: CanvasStatistics;
  compatibility: CompatibilityInfo;
}

/**
 * Canvas Statistics
 */
export interface CanvasStatistics {
  nodeCount: number;
  connectionCount: number;
  groupCount: number;
  complexity: 'simple' | 'medium' | 'complex' | 'advanced';
  estimatedExecutionTime?: number; // in seconds
}

/**
 * Compatibility Information
 */
export interface CompatibilityInfo {
  minimumVersion: string;
  requiredExtensions: string[];
  supportedFormats: string[];
}

/**
 * Serialized Node
 * Simplified node representation for storage
 */
export interface SerializedNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  size?: { width: number; height: number };
  properties: Record<string, any>;
  settings: NodeSettings;
  metadata: NodeMetadata;
}

/**
 * Node Settings
 */
export interface NodeSettings {
  viewMode: 'compact' | 'extended' | 'fullscreen';
  collapsed: boolean;
  visible: boolean;
  locked: boolean;
  customColor?: string;
  customTitle?: string;
}

/**
 * Node Metadata
 */
export interface NodeMetadata {
  createdAt: string;
  lastModified: string;
  executionCount: number;
  averageExecutionTime?: number;
  notes?: string;
}

/**
 * Serialized Connection
 */
export interface SerializedConnection {
  id: string;
  sourceNodeId: string;
  sourceSocketId: string;
  targetNodeId: string;
  targetSocketId: string;
  type: string;
  metadata: ConnectionMetadata;
}

/**
 * Connection Metadata
 */
export interface ConnectionMetadata {
  createdAt: string;
  dataFlowDirection: 'forward' | 'backward' | 'bidirectional';
  validationStatus: 'valid' | 'warning' | 'error';
  customStyle?: ConnectionStyle;
}

/**
 * Connection Style
 */
export interface ConnectionStyle {
  color?: string;
  width?: number;
  style?: 'solid' | 'dashed' | 'dotted';
  animated?: boolean;
}

/**
 * Serialized Group
 */
export interface SerializedGroup {
  id: string;
  name: string;
  description?: string;
  nodeIds: string[];
  position: { x: number; y: number };
  size: { width: number; height: number };
  color: string;
  collapsed: boolean;
  metadata: GroupMetadata;
  interface?: GroupInterface;
}

/**
 * Group Interface for Canvas-to-Node conversion
 */
export interface GroupInterface {
  inputs: InterfaceSocket[];
  outputs: InterfaceSocket[];
  properties: InterfaceProperty[];
}

/**
 * Interface Socket
 */
export interface InterfaceSocket {
  id: string;
  name: string;
  type: string;
  required: boolean;
  defaultValue?: any;
  description?: string;
  validation?: SocketValidation;
}

/**
 * Interface Property
 */
export interface InterfaceProperty {
  id: string;
  name: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'range' | 'color' | 'file';
  defaultValue: any;
  options?: PropertyOptions;
  validation?: PropertyValidation;
  description?: string;
}

/**
 * Property Options
 */
export interface PropertyOptions {
  min?: number;
  max?: number;
  step?: number;
  choices?: Array<{ value: any; label: string }>;
  multiSelect?: boolean;
  fileTypes?: string[];
}

/**
 * Socket Validation
 */
export interface SocketValidation {
  required: boolean;
  allowedTypes: string[];
  customValidator?: string; // Function name or expression
}

/**
 * Property Validation
 */
export interface PropertyValidation {
  required: boolean;
  min?: number;
  max?: number;
  pattern?: string;
  customValidator?: string;
}

/**
 * Group Metadata
 */
export interface GroupMetadata {
  createdAt: string;
  updatedAt: string;
  version: string;
  isTemplate: boolean;
  templateInfo?: TemplateInfo;
  executionStats?: ExecutionStats;
}

/**
 * Template Information
 */
export interface TemplateInfo {
  category: string;
  author: string;
  license?: string;
  repository?: string;
  documentation?: string;
  examples?: TemplateExample[];
}

/**
 * Template Example
 */
export interface TemplateExample {
  name: string;
  description: string;
  inputValues: Record<string, any>;
  expectedOutput?: any;
}

/**
 * Execution Statistics
 */
export interface ExecutionStats {
  totalExecutions: number;
  averageExecutionTime: number;
  lastExecuted?: string;
  successRate: number;
  commonErrors: string[];
}

/**
 * Canvas Layout Information
 */
export interface CanvasLayout {
  viewport: ViewportState;
  grid: GridSettings;
  theme: ThemeSettings;
  ui: UISettings;
}

/**
 * Viewport State
 */
export interface ViewportState {
  zoom: number;
  pan: { x: number; y: number };
  center: { x: number; y: number };
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

/**
 * Grid Settings
 */
export interface GridSettings {
  enabled: boolean;
  size: number;
  snapToGrid: boolean;
  showGrid: boolean;
  color: string;
  style: 'dots' | 'lines' | 'crosshatch';
}

/**
 * Theme Settings
 */
export interface ThemeSettings {
  name: string;
  colors: ThemeColors;
  fonts: ThemeFonts;
  spacing: ThemeSpacing;
}

/**
 * Theme Colors
 */
export interface ThemeColors {
  background: string;
  canvas: string;
  nodes: {
    default: string;
    selected: string;
    hover: string;
    border: string;
  };
  connections: {
    default: string;
    active: string;
    error: string;
  };
  ui: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    textSecondary: string;
  };
}

/**
 * Theme Fonts
 */
export interface ThemeFonts {
  primary: string;
  secondary: string;
  monospace: string;
  sizes: {
    small: number;
    medium: number;
    large: number;
    xlarge: number;
  };
}

/**
 * Theme Spacing
 */
export interface ThemeSpacing {
  small: number;
  medium: number;
  large: number;
  xlarge: number;
}

/**
 * UI Settings
 */
export interface UISettings {
  showMinimap: boolean;
  showNodePreview: boolean;
  showConnectionLabels: boolean;
  showExecutionIndicators: boolean;
  animationSpeed: 'slow' | 'normal' | 'fast' | 'instant';
  tooltipDelay: number;
}

/**
 * Canvas Settings
 */
export interface CanvasSettings {
  execution: ExecutionSettings;
  validation: ValidationSettings;
  performance: PerformanceSettings;
  debugging: DebuggingSettings;
}

/**
 * Execution Settings
 */
export interface ExecutionSettings {
  autoExecute: boolean;
  executionMode: 'sequential' | 'parallel' | 'optimized';
  maxConcurrentNodes: number;
  timeout: number;
  retryAttempts: number;
  cacheResults: boolean;
}

/**
 * Validation Settings
 */
export interface ValidationSettings {
  validateOnEdit: boolean;
  validateOnExecute: boolean;
  strictTypeChecking: boolean;
  warnOnMissingConnections: boolean;
  errorHandling: 'strict' | 'lenient' | 'ignore';
}

/**
 * Performance Settings
 */
export interface PerformanceSettings {
  renderingMode: 'full' | 'optimized' | 'minimal';
  maxNodesInView: number;
  virtualizeOffscreen: boolean;
  updateThrottling: number;
  memoryLimit: number;
}

/**
 * Debugging Settings
 */
export interface DebuggingSettings {
  enableDebugMode: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug' | 'trace';
  showExecutionTrace: boolean;
  recordExecutionHistory: boolean;
  maxHistoryEntries: number;
}

/**
 * Canvas-to-Node Conversion Options
 */
export interface CanvasToNodeOptions {
  generateInterface: boolean;
  preserveLayout: boolean;
  includeMetadata: boolean;
  optimizeConnections: boolean;
  validationLevel: 'none' | 'basic' | 'strict';
  compressionLevel: 'none' | 'basic' | 'aggressive';
  includeExecutionHistory: boolean;
}

/**
 * Node Definition for Canvas-as-Node
 */
export interface CanvasNodeDefinition {
  id: string;
  type: 'canvas-node';
  name: string;
  description: string;
  category: 'composite';
  serializedCanvas: SerializedCanvas;
  interface: GroupInterface;
  thumbnail?: string;
  version: string;
  metadata: CanvasNodeMetadata;
  implementation?: {
    type: string;
    data: string;
  };
}

/**
 * Canvas Node Metadata
 */
export interface CanvasNodeMetadata {
  originalCanvasId: string;
  conversionDate: string;
  sourceVersion: string;
  compressionRatio?: number;
  validationResults: ValidationResult[];
  dependencies: CanvasDependency[];
}

/**
 * Validation Result
 */
export interface ValidationResult {
  type: 'error' | 'warning' | 'info';
  message: string;
  nodeId?: string;
  connectionId?: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  autoFixable: boolean;
}

/**
 * Canvas Dependency
 */
export interface CanvasDependency {
  type: 'node-type' | 'extension' | 'model' | 'library';
  name: string;
  version?: string;
  required: boolean;
  source?: string;
  checksum?: string;
}

/**
 * Export Formats
 */
export enum ExportFormat {
  JSON = 'json',
  YAML = 'yaml',
  XML = 'xml',
  BINARY = 'binary',
  COMPRESSED = 'compressed'
}

/**
 * Import/Export Options
 */
export interface ImportExportOptions {
  format: ExportFormat;
  compression: boolean;
  includeMetadata: boolean;
  includeHistory: boolean;
  validateOnImport: boolean;
  backupOriginal: boolean;
  customFields?: Record<string, any>;
}